import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export type EntityType = 'tool' | 'resource' | 'job_board';

export interface Comment {
  id: string;
  user_id: string;
  entity_type: EntityType;
  entity_id: string;
  content: string;
  path: string;
  depth: number;
  is_deleted: boolean;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  user_email?: string;
  user_display_name?: string;
  replies?: Comment[];
}

export type CommentSort = 'top' | 'new';

export function useComments(entityType: EntityType, entityId: string) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState<CommentSort>('top');
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchComments = async (reset = false) => {
    try {
      setLoading(true);
      
      const query = supabase
        .from('comments')
        .select('*')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .eq('is_deleted', false)
        .limit(20);

      if (sortBy === 'new') {
        query.order('created_at', { ascending: false });
      } else {
        query.order('created_at', { ascending: true });
      }

      if (!reset && cursor) {
        query.gt('created_at', cursor);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Get user profiles separately
      const userIds = [...new Set(data?.map(c => c.user_id) || [])];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, email, display_name')
        .in('user_id', userIds);

      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);

      const commentsWithEmail = data?.map(comment => ({
        ...comment,
        path: comment.path as string,
        user_email: profileMap.get(comment.user_id)?.email || 'Anonymous',
        user_display_name: profileMap.get(comment.user_id)?.display_name || null,
      })) || [];

      if (reset) {
        setComments(buildCommentTree(commentsWithEmail));
        setCursor(null);
      } else {
        const allComments = [...comments, ...commentsWithEmail];
        setComments(buildCommentTree(allComments));
      }

      setHasMore(data?.length === 20);
      if (data?.length > 0) {
        setCursor(data[data.length - 1].created_at);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast({
        title: 'Error',
        description: 'Failed to load comments',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const buildCommentTree = (flatComments: Comment[]): Comment[] => {
    const commentMap = new Map<string, Comment>();
    const rootComments: Comment[] = [];

    // Initialize all comments with empty replies array
    flatComments.forEach(comment => {
      commentMap.set(comment.id, { ...comment, replies: [] });
    });

    // Build the tree structure
    flatComments.forEach(comment => {
      const commentWithReplies = commentMap.get(comment.id)!;
      
      if (comment.depth === 0) {
        rootComments.push(commentWithReplies);
      } else {
        // Find parent by path
        const pathParts = comment.path.split('.');
        if (pathParts.length > 1) {
          const parentId = pathParts[pathParts.length - 2];
          const parent = commentMap.get(parentId);
          if (parent) {
            parent.replies = parent.replies || [];
            parent.replies.push(commentWithReplies);
          }
        }
      }
    });

    return rootComments;
  };

  const submitComment = async (content: string, parentPath?: string) => {
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to comment',
        variant: 'destructive',
      });
      return;
    }

    if (content.trim().length < 1 || content.trim().length > 2000) {
      toast({
        title: 'Invalid Comment',
        description: 'Comment must be between 1 and 2000 characters',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);

      const { data, error } = await supabase
        .from('comments')
        .insert({
          user_id: user.id,
          entity_type: entityType,
          entity_id: entityId,
          content: content.trim(),
          path: parentPath || null,
        })
        .select('*')
        .single();

      if (error) throw error;

      // Get user profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('email, display_name')
        .eq('user_id', user.id)
        .single();

      const newComment: Comment = {
        ...data,
        path: data.path as string,
        user_email: profile?.email || 'Anonymous',
        user_display_name: profile?.display_name || null,
        replies: [],
      };

      // Add to comments list
      setComments(prevComments => {
        if (!parentPath) {
          return [newComment, ...prevComments];
        } else {
          // Add as reply to existing comment
          const updatedComments = addReplyToTree([...prevComments], newComment, parentPath);
          return updatedComments;
        }
      });

      toast({
        title: 'Comment Posted',
        description: 'Your comment has been added',
      });

      return true;
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast({
        title: 'Error',
        description: 'Failed to post comment',
        variant: 'destructive',
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const addReplyToTree = (tree: Comment[], newReply: Comment, parentPath: string): Comment[] => {
    return tree.map(comment => {
      if (comment.path === parentPath) {
        return {
          ...comment,
          replies: [newReply, ...(comment.replies || [])],
        };
      } else if (comment.replies?.length > 0) {
        return {
          ...comment,
          replies: addReplyToTree(comment.replies, newReply, parentPath),
        };
      }
      return comment;
    });
  };

  const deleteComment = async (commentId: string) => {
    if (!user) return;

    try {
      // Hard delete the comment from the database
      const { error } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Remove from local state completely
      setComments(prevComments => removeCommentFromTree(prevComments, commentId));

      toast({
        title: 'Comment Deleted',
        description: 'Your comment has been permanently deleted',
      });
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete comment',
        variant: 'destructive',
      });
    }
  };

  const removeCommentFromTree = (tree: Comment[], commentId: string): Comment[] => {
    return tree.filter(comment => {
      if (comment.id === commentId) {
        return false; // Remove this comment
      }
      // Keep the comment but filter its replies
      if (comment.replies?.length > 0) {
        comment.replies = removeCommentFromTree(comment.replies, commentId);
      }
      return true;
    });
  };

  const editComment = async (commentId: string, newContent: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('comments')
        .update({
          content: newContent,
          updated_at: new Date().toISOString(),
        })
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Update local state
      setComments(prevComments => 
        updateCommentInTree(prevComments, commentId, { 
          content: newContent,
          updated_at: new Date().toISOString(),
        })
      );

      toast({
        title: 'Comment Updated',
        description: 'Your comment has been updated',
      });
    } catch (error) {
      console.error('Error editing comment:', error);
      toast({
        title: 'Error',
        description: 'Failed to update comment',
        variant: 'destructive',
      });
    }
  };

  const updateCommentInTree = (tree: Comment[], commentId: string, updates: Partial<Comment>): Comment[] => {
    return tree.map(comment => {
      if (comment.id === commentId) {
        return { ...comment, ...updates };
      } else if (comment.replies?.length > 0) {
        return {
          ...comment,
          replies: updateCommentInTree(comment.replies, commentId, updates),
        };
      }
      return comment;
    });
  };

  const reportComment = async (commentId: string, reason: string) => {
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to report comments',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('comment_reports')
        .insert({
          comment_id: commentId,
          reporter_user_id: user.id,
          reason,
        });

      if (error) throw error;

      toast({
        title: 'Comment Reported',
        description: 'Thank you for reporting this comment',
      });
    } catch (error) {
      console.error('Error reporting comment:', error);
      toast({
        title: 'Error',
        description: 'Failed to report comment',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    fetchComments(true);
  }, [entityType, entityId, sortBy]);

  return {
    comments,
    loading,
    submitting,
    sortBy,
    hasMore,
    setSortBy,
    submitComment,
    deleteComment,
    editComment,
    reportComment,
    loadMore: () => fetchComments(false),
    refresh: () => fetchComments(true),
  };
}